/* Native navigation enhancements and the bridge to the existing mobile drawer. */
(() => {
  if (window.mwHeaderBlocks) return;
  window.mwHeaderBlocks = true;
  let controller;
  let resizeObserver;
  let mobileObserver;
  let documentObserver;
  let paletteObserver;
  let mountedTarget;
  let mountedTemplate;
  let root;
  let scheduledMount;
  const paletteProperties = [
    '--mw-store-bg',
    '--mw-store-text',
    '--mw-header-top-background',
    '--mw-header-top-color',
    '--mw-header-surface',
    '--mw-header-foreground'
  ];

  function scheduleMobileMenuMount() {
    if (scheduledMount) return;
    scheduledMount = requestAnimationFrame(() => {
      scheduledMount = null;
      mountMobileMenu();
    });
  }

  function containsMobileNavigation(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    return node.matches('.section-mobile-navigation, #mobile-navigation, #menu-level-0')
      || node.querySelector('.section-mobile-navigation, #mobile-navigation, #menu-level-0');
  }

  function syncMobilePalette(drawer = document.getElementById('mobile-navigation')) {
    if (!root || !drawer) return;
    const source = getComputedStyle(root);
    paletteProperties.forEach(property => {
      const value = source.getPropertyValue(property).trim();
      if (value) drawer.style.setProperty(property, value);
      else drawer.style.removeProperty(property);
    });
  }

  function mountMobileMenu() {
    const nextRoot = document.querySelector('[data-mw-header]');
    if (nextRoot !== root) {
      root = nextRoot;
      mountedTemplate = null;
    }
    const target = document.getElementById('menu-level-0');
    if (!target) return;
    const template = root?.querySelector('[data-header-mobile-template]');
    if (target === mountedTarget && template === mountedTemplate && target.querySelector('.mw-header-mobile-menu')) {
      syncMobilePalette(target.closest('#mobile-navigation'));
      return;
    }
    mountedTarget = target;
    mountedTemplate = template;
    target.querySelector('.mw-header-mobile-menu')?.remove();
    delete target.dataset.headerSharedMenu;
    if (!template) return;
    const clone = template.content.cloneNode(true);
    if (!clone.querySelector('.mw-header-menu__item :is(a[href], summary)')) return;
    target.prepend(clone);
    if (!target.querySelector('.mw-header-mobile-menu')) return;
    target.dataset.headerSharedMenu = '';
    syncMobilePalette(target.closest('#mobile-navigation'));
  }

  function closeBranch(branch, focus = false) {
    if (!branch) return;
    branch.querySelectorAll('details[open]').forEach(child => { child.open = false; });
    branch.open = false;
    if (focus) branch.querySelector('summary')?.focus();
  }

  function initialize() {
    controller?.abort();
    resizeObserver?.disconnect();
    mobileObserver?.disconnect();
    documentObserver?.disconnect();
    paletteObserver?.disconnect();
    root = document.querySelector('[data-mw-header]');
    controller = new AbortController();
    const { signal } = controller;
    mountMobileMenu();
    if (!root) return;
    const mobileSection = document.querySelector('.section-mobile-navigation');
    if (mobileSection) {
      mobileObserver = new MutationObserver(scheduleMobileMenuMount);
      mobileObserver.observe(mobileSection, { childList: true, subtree: true });
    }
    documentObserver = new MutationObserver(mutations => {
      if (mutations.some(mutation => [...mutation.addedNodes].some(containsMobileNavigation))) scheduleMobileMenuMount();
    });
    documentObserver.observe(document.body, { childList: true, subtree: true });
    paletteObserver = new MutationObserver(() => syncMobilePalette());
    paletteObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    paletteObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    document.querySelectorAll('[data-country-flag]').forEach(flag => {
      const code = flag.dataset.countryFlag;
      if (/^[A-Z]{2}$/.test(code)) flag.textContent = String.fromCodePoint(...[...code].map(letter => letter.charCodeAt(0) + 127397));
    });
    document.addEventListener('keydown', event => {
      const drawer = document.getElementById('mobile-navigation');
      const mobileOpen = window.Alpine?.store('xMobileNav')?.show;
      if (mobileOpen && event.key === 'Tab' && drawer) {
        const focusable = [...drawer.querySelectorAll('button, a[href], summary, input, select')].filter(el => el.getClientRects().length && !el.disabled);
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
      if (event.key !== 'Escape') return;
      const nav = event.target.closest('.mw-header-menu');
      if (nav) {
        const branch = event.target.closest('details[open]') || nav.querySelector('details[open]');
        if (branch) {
          event.preventDefault();
          event.stopPropagation();
          closeBranch(branch, true);
          return;
        }
      } else if (root.contains(event.target)) {
        const menu = window.Alpine?.store('xHeaderMenu');
        menu?.closeMenu();
        if (menu) menu.openHamburgerMenu = false;
        root.querySelector('.mw-header__menu-trigger')?.focus();
      }
      if (mobileOpen) window.Alpine.store('xMobileNav').close();
    }, { signal, capture: true });
    document.addEventListener('click', event => {
      if (event.target.closest('#header-mobile-trigger')) {
        syncMobilePalette();
        requestAnimationFrame(() => document.querySelector('#mobile-navigation button')?.focus());
      }
      const back = event.target.closest('[data-menu-back]');
      if (back) {
        const nav = back.closest('.mw-header-menu');
        const branches = [...nav.querySelectorAll('details[open]')];
        closeBranch(branches.at(-1), true);
      }
      document.querySelectorAll('.mw-header-menu:not(.mw-header-menu--mobile) details[open]').forEach(branch => {
        if (!branch.contains(event.target)) closeBranch(branch);
      });
    }, { signal });
    document.addEventListener('eurus:popup:close', () => {
      if (document.getElementById('mobile-navigation')?.contains(document.activeElement)) root.querySelector('#header-mobile-trigger')?.focus();
    }, { signal });
    document.addEventListener('toggle', event => {
      const branch = event.target;
      if (!branch.matches?.('.mw-header-menu__branch') || !branch.open) return;
      const nav = branch.closest('.mw-header-menu');
      if (!nav.classList.contains('mw-header-menu--mobile') || nav.dataset.accordion === 'false') {
        for (const sibling of branch.closest('ul').children) {
          const other = sibling.querySelector(':scope > details');
          if (other !== branch) closeBranch(other);
        }
      }
    }, { signal, capture: true });
    root.addEventListener('pointerover', event => {
      if (!matchMedia('(min-width: 1024px) and (hover: hover)').matches) return;
      const nav = event.target.closest('.mw-header-menu[data-motion="hover"]');
      const branch = event.target.closest('details');
      if (nav && branch) branch.open = true;
    }, { signal });
    root.querySelectorAll('.mw-header-menu[data-motion="hover"]').forEach(nav => {
      nav.addEventListener('pointerleave', () => {
        if (!nav.contains(document.activeElement)) nav.querySelectorAll('details[open]').forEach(branch => closeBranch(branch));
      }, { signal });
    });
    if (typeof ResizeObserver !== 'undefined') {
      let previousHeight = 0;
      resizeObserver = new ResizeObserver(entries => {
        const height = entries[0].target.offsetHeight;
        if (height === previousHeight) return;
        previousHeight = height;
        const menu = window.Alpine?.store('xHeaderMenu');
        if (!menu?.initialized) return;
        menu.stickyOffsetHeight = height;
        menu.initVariable(root.closest('.section-header'), root, root.dataset.sticky, document.getElementById('x-announcement'));
      });
      resizeObserver.observe(root.querySelector('#sticky-header-content'));
    }
  }

  function refreshAnnouncementHeader(event) {
    if (!event.target.matches?.('.section-announcement')) return;
    // Section unload fires before removal; measure the replacement DOM next frame.
    requestAnimationFrame(() => {
      const header = document.querySelector('[data-mw-header]');
      const menu = window.Alpine?.store('xHeaderMenu');
      if (!header || !menu) return;
      menu.sectionId = header.dataset.sectionId;
      menu.initSticky(header, header.dataset.sticky, header.dataset.transparent === 'true');
    });
  }

  document.addEventListener('shopify:section:load', initialize);
  for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
    document.addEventListener(event, refreshAnnouncementHeader);
  }
  document.addEventListener('shopify:section:reorder', scheduleMobileMenuMount);
  document.addEventListener('alpine:init', scheduleMobileMenuMount);
  document.addEventListener('init-run', scheduleMobileMenuMount);
  document.addEventListener('shopify:section:unload', event => {
    if (!root || !event.target.contains(root)) return;
    controller?.abort();
    resizeObserver?.disconnect();
    mobileObserver?.disconnect();
    documentObserver?.disconnect();
    paletteObserver?.disconnect();
    window.Alpine?.store('xHeaderMenu')?.reset();
    root = null;
    mountMobileMenu();
  });
  document.addEventListener('shopify:block:select', event => {
    const block = event.target.closest('.mw-header__slot--menu');
    if (block) block.querySelector('details')?.setAttribute('open', '');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
